import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  Trophy,
  Coins,
  BookOpen,
  AlertCircle,
  RefreshCw,
  FileText,
  Target,
  Award,
  Brain,
  Flame,
  Zap,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Edit3,
  AlertTriangle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import "../styles/extremeGame.css";
import { API_BASE, DEV_MODE } from "../config";
import gameConfig from "../data/gameConfig.json";
import { calcTimeBonusPct } from "../utils/scoring";
import apiFetch from "../apiFetch";

const MODE_CFG = gameConfig.extreme;

const ExtremeGame = () => {
  const navigate = useNavigate();
  // Game state management
  const [gameState, setGameState] = useState("difficulty-select"); // difficulty-select, loading, playing, reviewing, results
  const [difficulty, setDifficulty] = useState(null);
  const [passage, setPassage] = useState("");
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [writtenQuestion, setWrittenQuestion] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [writtenAnswer, setWrittenAnswer] = useState("");
  const [timeLeft, setTimeLeft] = useState(MODE_CFG.time);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markingResults, setMarkingResults] = useState(null);
  const [showPassage, setShowPassage] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(0);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const submitModalRef = useRef(null);

  useEffect(() => {
    if (!showSubmitModal) return;
    submitModalRef.current?.querySelector("button")?.focus();
    const handler = (e) => { if (e.key === "Escape") setShowSubmitModal(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showSubmitModal]);

  const difficultyLevels = {
    1: {
      title: "Beyond Expert",
      description: "University English Literature Graduate Level",
      icon: <Brain className="difficulty-icon" />,
      color: "difficulty-1",
    },
    2: {
      title: "Transcendent",
      description: "Beyond PhD - Humanly High Vocabulary",
      icon: <Flame className="difficulty-icon" />,
      color: "difficulty-2",
    },
    3: {
      title: "Impossible",
      description: "The Most Extreme Obscure Challenge",
      icon: <Zap className="difficulty-icon" />,
      color: "difficulty-3",
    },
  };

  // Initialize game
  useEffect(() => {
    if (DEV_MODE) return;
    if (gameState === "playing" && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === "playing") {
      handleSubmit();
    }
  }, [timeLeft, gameState]);

  const selectDifficulty = (level) => {
    setDifficulty(level);
    initializeGame(level);
  };

  const initializeGame = async (difficultyLevel) => {
    setGameState("loading");
    setLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/api/generate-extreme`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          difficulty: difficultyLevel,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.passage || !data.mcqQuestions || !data.writtenQuestion) {
        throw new Error("Invalid data received from API");
      }

      setPassage(data.passage);
      setMcqQuestions(data.mcqQuestions);
      setWrittenQuestion(data.writtenQuestion);

      // Initialize answers
      const initialMcqAnswers = {};
      data.mcqQuestions.forEach((_, index) => {
        initialMcqAnswers[index] = "";
      });
      setMcqAnswers(initialMcqAnswers);
      setWrittenAnswer("");

      setGameState("playing");
      setTimeLeft(420);
      setCurrentQuestion(0);
    } catch (error) {
      console.error("Error fetching game data:", error);
      setError(
        "Failed to generate extreme comprehension game. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMcqAnswerChange = (questionIndex, answer) => {
    setMcqAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleWrittenAnswerChange = (answer) => {
    setWrittenAnswer(answer);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setGameState("reviewing");

    try {
      const response = await apiFetch(`/api/mark-extreme`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          difficulty: difficulty,
          mcqQuestions: mcqQuestions,
          writtenQuestion: writtenQuestion,
          mcqAnswers: mcqAnswers,
          writtenAnswer: writtenAnswer,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      setMarkingResults(results);

      const coins = calculateCoinsEarned(
        results.totalScore || 0,
        timeLeft,
        difficulty,
      );
      setCoinsEarned(coins);
      await addCoinsAndXPToUser(coins);

      const modeName = `ext${difficulty}`;
      //await saveStatsToDatabase(results.totalScore || 0, modeName);
      await saveStatsToDatabase((results.totalScore || 0) * 10, modeName); //x 10 for normalization

      setGameState("results");
    } catch (error) {
      console.error("Error marking answers:", error);
      setError("Failed to mark answers. Please try again.");
      setGameState("playing");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ---------- Coins: tweakable knobs ----------
  const calculateCoinsEarned = (score, remainingSeconds, difficultyLevel) => {
    const accuracy = score / MODE_CFG.maxScore;
    const bonusPct = calcTimeBonusPct(accuracy, remainingSeconds, MODE_CFG.time, MODE_CFG.maxTimeBonusPct);
    const diffMult = MODE_CFG.difficultyMultipliers[String(difficultyLevel)] || 1;
    return Math.floor(score * MODE_CFG.baseCoinsPerPoint * diffMult * (1 + bonusPct) * MODE_CFG.coinsMultiplier);
  };

  // Persist coins
  const addCoinsAndXPToUser = async (coinsToAdd) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || coinsToAdd <= 0) return;

    try {
      // 1) Update coins
      const res = await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsToAdd }),
      });
      if (!res.ok) {
        console.error("Failed to update coins:", await res.text());
        return;
      }

      // 2) Update XP (coins × 5)
      const xpRes = await apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsToAdd * MODE_CFG.xpMultiplier }),
      });
      if (!xpRes.ok) {
        console.error("❌ Failed to update XP:", await xpRes.text());
        return;
      }
    } catch (err) {
      console.error("❌ Error updating coins/XP:", err);
    }
  };

  //=======================
  // saving game data stats
  //========================

  const saveStatsToDatabase = async (score, modeName) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || typeof score !== "number") return;

    try {
      const res = await apiFetch(`/api/sessions/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          mode: modeName, // e.g., ext1, ext2, ext3
          score,
        }),
      });
      if (!res.ok) {
        console.error("Failed to save Extreme game stats:", await res.text());
      }
    } catch (err) {
      console.error("Error saving Extreme game stats:", err);
    }
  };

  //--------------------------

  const getTotalQuestions = () => mcqQuestions.length + 1;
  const getCurrentQuestionType = () =>
    currentQuestion < mcqQuestions.length ? "mcq" : "written";
  const getAnsweredCount = () =>
    Object.values(mcqAnswers).filter((a) => a).length + (writtenAnswer ? 1 : 0);

  const navigateToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const nextQuestion = () => {
    if (currentQuestion < getTotalQuestions() - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrentReviewQuestion(0);
  };

  const exitReview = () => {
    setReviewMode(false);
  };

  // Difficulty Selection Screen
  if (gameState === "difficulty-select") {
    return (
      <div className="difficulty-select-screen">
        <div className="difficulty-content">
          <div className="difficulty-header">
            <FileText className="main-icon" />
            <h1>Extreme Reading Challenge</h1>
            <p>Select your difficulty level</p>
          </div>

          <div className="difficulty-options">
            {Object.entries(difficultyLevels).map(([level, config]) => (
              <button
                key={level}
                className={`difficulty-option ${config.color}`}
                onClick={() => selectDifficulty(parseInt(level))}
              >
                {config.icon}
                <h3>{config.title}</h3>
                <p>{config.description}</p>
                <span className="level-indicator">Level {level}</span>
              </button>
            ))}
          </div>

          <div className="game-info">
            <div className="info-item">
              <Target className="info-icon" />
              <span>5 MCQ + 1 Written Question</span>
            </div>
            <div className="info-item">
              <Clock className="info-icon" />
              <span>7 Minutes</span>
            </div>
            <div className="info-item">
              <Star className="info-icon" />
              <span>10 Marks Total</span>
            </div>
          </div>

          <button className="back-button" onClick={() => window.history.back()}>
            <ArrowLeft /> Back to Menu
          </button>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (gameState === "loading") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="difficulty-indicator">
            {difficultyLevels[difficulty]?.icon}
            <h2>{difficultyLevels[difficulty]?.title}</h2>
          </div>
          <BookOpen className={`spinner-icon ${loading ? "spinning" : ""}`} />
          <p>
            {loading
              ? "Generating extreme difficulty questions..."
              : "Preparing your challenge..."}
          </p>
          {error && (
            <div className="error-message">
              <AlertCircle />
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Reviewing Screen
  if (gameState === "reviewing") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <Edit3 className="spinner-icon spinning" />
          <p>Analyzing your responses...</p>
          <p className="sub-text">
            This may take a moment for extreme difficulty marking
          </p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === "results") {
    if (reviewMode) {
      return (
        <div className="review-screen extreme-review-screen">
          <div className="review-header">
            <button className="back-to-results" onClick={exitReview}>
              <ArrowLeft /> Back to Results
            </button>
            <h2>Question Review</h2>
            <div className="passage-toggle">
              <button
                className={`toggle-button ${showPassage ? "active" : ""}`}
                onClick={() => setShowPassage(!showPassage)}
              >
                {showPassage ? <EyeOff /> : <Eye />}
                {showPassage ? "Hide" : "Show"} Passage
              </button>
            </div>
          </div>

          <div className="review-content">
            {showPassage && (
              <div className="passage-review">
                <h3>Reading Passage</h3>
                <div className="passage-text">{passage}</div>
              </div>
            )}

            <div className="question-review">
              <div className="review-navigation">
                {[...mcqQuestions, writtenQuestion].map((_, index) => (
                  <button
                    key={index}
                    className={`review-nav-button ${index === currentReviewQuestion ? "active" : ""}`}
                    onClick={() => setCurrentReviewQuestion(index)}
                  >
                    Q{index + 1}
                  </button>
                ))}
              </div>

              <div className="review-question">
                {currentReviewQuestion < mcqQuestions.length ? (
                  <McqReview
                    question={mcqQuestions[currentReviewQuestion]}
                    userAnswer={mcqAnswers[currentReviewQuestion]}
                    result={markingResults?.mcqResults?.[currentReviewQuestion]}
                    questionNumber={currentReviewQuestion + 1}
                  />
                ) : (
                  <WrittenReview
                    question={writtenQuestion}
                    userAnswer={writtenAnswer}
                    result={markingResults?.writtenResult}
                    questionNumber={currentReviewQuestion + 1}
                  />
                )}
              </div>

              <div className="review-controls">
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.max(0, currentReviewQuestion - 1),
                    )
                  }
                  disabled={currentReviewQuestion === 0}
                  className="nav-button"
                >
                  <ChevronLeft /> Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.min(
                        getTotalQuestions() - 1,
                        currentReviewQuestion + 1,
                      ),
                    )
                  }
                  disabled={currentReviewQuestion === getTotalQuestions() - 1}
                  className="nav-button"
                >
                  Next <ChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="results-screen">
        <div className="results-content">
          <div className="difficulty-badge">
            {difficultyLevels[difficulty]?.icon}
            <span>{difficultyLevels[difficulty]?.title}</span>
          </div>

          <Trophy className="trophy-icon" />
          <h2>Challenge Complete!</h2>

          <div className="final-stats">
            <div className="stat-item">
              <Star className="stat-icon" />
              <div>
                <div className="stat-label">Final Score</div>
                <div className="stat-value">
                  {markingResults?.totalScore || 0}/10
                </div>
              </div>
            </div>
            <div className="stat-item">
              <Clock className="stat-icon" />
              <div>
                <div className="stat-label">Time Used</div>
                <div className="stat-value">{formatTime(420 - timeLeft)}</div>
              </div>
            </div>
            <div className="stat-item">
              <Coins className="stat-icon" />
              <div>
                <div className="stat-label">Coins Earned</div>
                <div className="stat-value">{coinsEarned}</div>
              </div>
            </div>
          </div>

          <div className="results-summary">
            <div className="summary-item">
              <CheckCircle className="summary-icon correct" />
              <span>MCQ Correct: {markingResults?.mcqCorrect || 0}/5</span>
            </div>
            <div className="summary-item">
              <FileText className="summary-icon" />
              <span>
                Written: {markingResults?.writtenResult?.marksAwarded || 0}/5
              </span>
            </div>
          </div>

          <div className="action-buttons">
            <button className="primary-button" onClick={startReview}>
              <Eye /> Review Answers
            </button>
            <button
              className="secondary-button"
              onClick={() => {
                setGameState("difficulty-select");
                setDifficulty(null);
                setMarkingResults(null);
                setReviewMode(false);
                setCoinsEarned(0);
              }}
            >
              <RefreshCw /> New Challenge
            </button>
            <button
              className="tertiary-button"
              onClick={() => window.history.back()}
            >
              <ArrowLeft /> Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Game Screen
  return (
    <div className="extreme-game">
      <GameHeader
        difficulty={difficulty}
        difficultyConfig={difficultyLevels[difficulty]}
        timeLeft={timeLeft}
        formatTime={formatTime}
        currentQuestion={currentQuestion}
        totalQuestions={getTotalQuestions()}
        answeredCount={getAnsweredCount()}
        onBack={() => navigate("/games/comprehension")}
      />

      {error && (
        <div className="error-banner">
          <AlertCircle />
          {error}
        </div>
      )}

      <div className="game-layout">
        <div className="passage-panel">
          <div className="passage-header">
            <BookOpen className="passage-icon" />
            <h3>Reading Passage</h3>
            <button
              className="passage-toggle"
              onClick={() => setShowPassage(!showPassage)}
            >
              {showPassage ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {showPassage && (
            <div className="passage-content">
              <div className="passage-text">{passage}</div>
            </div>
          )}
        </div>

        <div className="questions-panel">
          <QuestionNavigation
            totalQuestions={getTotalQuestions()}
            currentQuestion={currentQuestion}
            mcqAnswers={mcqAnswers}
            writtenAnswer={writtenAnswer}
            onNavigate={navigateToQuestion}
          />

          <div className="question-content">
            {getCurrentQuestionType() === "mcq" ? (
              <McqQuestion
                question={mcqQuestions[currentQuestion]}
                questionIndex={currentQuestion}
                userAnswer={mcqAnswers[currentQuestion] || ""}
                onAnswerChange={handleMcqAnswerChange}
              />
            ) : (
              <WrittenQuestion
                question={writtenQuestion}
                userAnswer={writtenAnswer}
                onAnswerChange={handleWrittenAnswerChange}
              />
            )}
          </div>

          <div className="question-controls">
            <button
              className="nav-button"
              onClick={previousQuestion}
              disabled={currentQuestion === 0}
            >
              <ChevronLeft /> Previous
            </button>

            <button
              className="nav-button"
              onClick={nextQuestion}
              disabled={currentQuestion === getTotalQuestions() - 1}
            >
              Next <ChevronRight />
            </button>

            <button
              className="submit-button"
              onClick={() => {
                const allAnswered =
                  Object.values(mcqAnswers).filter(Boolean).length === mcqQuestions.length &&
                  writtenAnswer.trim().length > 0;
                if (allAnswered || timeLeft <= 0) {
                  handleSubmit();
                } else {
                  setShowSubmitModal(true);
                }
              }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Finish & Submit"}
            </button>
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="submit-confirm-overlay" onClick={() => setShowSubmitModal(false)}>
          <div
            className="submit-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="extreme-submit-title"
            ref={submitModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="submit-confirm-icon" aria-hidden="true">
              <AlertTriangle size={26} />
            </div>
            <h3 id="extreme-submit-title">Unanswered Questions</h3>
            <p>
              You have{" "}
              {getTotalQuestions() - getAnsweredCount()}{" "}
              unanswered question(s). Are you sure you want to submit?
            </p>
            <div className="submit-confirm-actions">
              <button className="submit-confirm-back" onClick={() => setShowSubmitModal(false)}>
                Go Back
              </button>
              <button className="submit-confirm-go" onClick={() => { setShowSubmitModal(false); handleSubmit(); }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components
const GameHeader = ({
  difficulty,
  difficultyConfig,
  timeLeft,
  formatTime,
  currentQuestion,
  totalQuestions,
  answeredCount,
  onBack,
}) => (
  <header className="game-header">
    <div className="header-left">
      <div className="difficulty-indicator">
        {difficultyConfig?.icon}
        <span>{difficultyConfig?.title}</span>
      </div>
      <h1>Extreme Reading Challenge</h1>
    </div>
    <div className="header-right">
      <div className="header-stats">
        <div className="progress-indicator">
          <span>Question {currentQuestion + 1} of {totalQuestions}</span>
          <span>({answeredCount}/{totalQuestions} answered)</span>
        </div>
        <div className={`timer ${timeLeft < 60 ? "urgent" : ""}`}>
          <Clock /> {formatTime(timeLeft)}
        </div>
      </div>
      <button className="header-back-btn" onClick={onBack}>
        <ArrowLeft size={14} /> Back to Dashboard
      </button>
    </div>
  </header>
);

const QuestionNavigation = ({
  totalQuestions,
  currentQuestion,
  mcqAnswers,
  writtenAnswer,
  onNavigate,
}) => (
  <div className="question-navigation">
    <h4>Questions</h4>
    <div className="question-tabs">
      {Array.from({ length: totalQuestions }, (_, index) => {
        const isAnswered = index < 5 ? mcqAnswers[index] : writtenAnswer;
        const questionType = index < 5 ? "MCQ" : "Written";
        const marks = index < 5 ? 1 : 5;

        return (
          <button
            key={index}
            className={`question-tab ${index === currentQuestion ? "active" : ""} ${isAnswered ? "answered" : ""}`}
            onClick={() => onNavigate(index)}
          >
            <span className="question-number">{index + 1}</span>
            <span className="question-type">{questionType}</span>
            <span className="question-marks">({marks}m)</span>
            {isAnswered && <CheckCircle className="answered-icon" />}
          </button>
        );
      })}
    </div>
  </div>
);

const McqQuestion = ({
  question,
  questionIndex,
  userAnswer,
  onAnswerChange,
}) => (
  <div className="mcq-question">
    <div className="question-header">
      <h4>Question {questionIndex + 1}</h4>
      <span className="marks-indicator">[1 mark]</span>
    </div>

    <div className="question-text">{question.question}</div>

    <div className="options-container">
      {Object.entries(question.options).map(([key, text]) => (
        <label key={key} className="option-item">
          <input
            type="radio"
            name={`mcq-${questionIndex}`}
            value={key}
            checked={userAnswer === key}
            onChange={(e) => onAnswerChange(questionIndex, e.target.value)}
          />
          <span className="option-key">{key})</span>
          <span className="option-text">{text}</span>
        </label>
      ))}
    </div>
  </div>
);

const WrittenQuestion = ({ question, userAnswer, onAnswerChange }) => (
  <div className="written-question">
    <div className="question-header">
      <h4>Question 6 (Written Response)</h4>
      <span className="marks-indicator">[5 marks]</span>
    </div>

    <div className="question-text">{question.question}</div>

    <div className="answer-section">
      <textarea
        className="answer-input"
        value={userAnswer}
        onChange={(e) => onAnswerChange(e.target.value)}
        placeholder="Write your detailed answer here..."
        rows={10}
      />
      <div className="answer-info">
        <span className="character-count">{userAnswer.length} characters</span>
        {question.markingPoints && (
          <div className="marking-points">
            <h5>Marking Criteria:</h5>
            <ul>
              {question.markingPoints.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

const McqReview = ({ question, userAnswer, result, questionNumber }) => (
  <div className="mcq-review">
    <div className="review-header">
      <h4>Question {questionNumber}</h4>
      <div
        className={`result-badge ${result?.correct ? "correct" : "incorrect"}`}
      >
        {result?.correct ? <CheckCircle /> : <XCircle />}
        {result?.correct ? "Correct" : "Incorrect"}
      </div>
    </div>

    <div className="question-text">{question.question}</div>

    <div className="options-review">
      {Object.entries(question.options).map(([key, text]) => (
        <div
          key={key}
          className={`option-review ${key === question.correct ? "correct" : ""} ${key === userAnswer ? "selected" : ""}`}
        >
          <span className="option-key">{key})</span>
          <span className="option-text">{text}</span>
          {key === question.correct && <CheckCircle className="correct-icon" />}
          {key === userAnswer && key !== question.correct && (
            <XCircle className="incorrect-icon" />
          )}
        </div>
      ))}
    </div>

    <div className="explanation">
      <h5>Explanation:</h5>
      <p>{result?.explanation}</p>
    </div>
  </div>
);

const WrittenReview = ({ question, userAnswer, result, questionNumber }) => (
  <div className="written-review">
    <div className="review-header">
      <h4>Question {questionNumber} (Written Response)</h4>
      <div className="marks-awarded">
        <Star className="marks-icon" />
        {result?.marksAwarded || 0}/5 marks
      </div>
    </div>

    <div className="question-text">{question.question}</div>

    <div className="answer-review">
      <h5>Your Answer:</h5>
      <div className="user-answer">{userAnswer || "No answer provided"}</div>
    </div>

    <div className="marking-breakdown">
      <h5>Marking Breakdown:</h5>
      <p>{result?.explanation}</p>
    </div>

    <div className="feedback">
      <h5>Feedback:</h5>
      <p>{result?.feedback}</p>
    </div>
  </div>
);

export default ExtremeGame;
